/**
 * Gets a generated file onto the shopkeeper's phone.
 *
 * Two routes, because neither works everywhere. The share sheet is the one that
 * matters on a handset: it is how Android offers "save to Photos", WhatsApp and
 * the printer in one list, which is what a shopkeeper wants with a bill — send
 * it to the customer *or* keep it. Desktop browsers mostly do not implement
 * file sharing, so there `<a download>` is the whole story.
 *
 * `<a download>` is also the fallback inside the WebView shell, where it needs
 * a `DownloadListener` on the native side to land anywhere. Until the shell
 * grows one, the share sheet is what carries this on a phone — it is available
 * in Android's WebView through Chrome's implementation.
 *
 * Returns false when neither route took it, so the caller can say so rather
 * than leaving the shopkeeper looking for a file that was never saved.
 */
export async function saveFile(blob: Blob, filename: string): Promise<boolean> {
  const file = new File([blob], filename, { type: blob.type });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return true;
    } catch (error) {
      // Dismissing the sheet is not a failure, and must not fall through to a
      // download the shopkeeper did not ask for a second time.
      if (error instanceof DOMException && error.name === 'AbortError') return true;
      // Anything else — most likely NotAllowedError, because sharing needs
      // transient user activation and drawing the bill happened between the
      // tap and this call — falls through to the download below.
    }
  }

  return download(blob, filename);
}

function download(blob: Blob, filename: string): boolean {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  } catch {
    return false;
  } finally {
    // Given to the browser first; revoking immediately cancels the save in
    // some engines, and a minute is far longer than any of them need.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}
